// SaaS-clean sidebar shell used by the parent, teacher, and school-admin
// roles. Burgundy sidebar + cream content, mirrors apps/admin's chrome but
// driven by per-role nav configs and fully i18n. RTL-safe via logical props.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Button } from '@fajrarabi/ui';
import { Link, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { LangSwitcher } from './LangSwitcher';
import { NotificationBell } from './NotificationBell';
import { RoleSwitcher } from './RoleSwitcher';

import { useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';

/** Route paths the sidebar can link to (all take only the {locale} param). */
export type NavTo =
  | '/$locale'
  | '/$locale/kids'
  | '/$locale/progress'
  | '/$locale/messages'
  | '/$locale/billing'
  | '/$locale/account'
  | '/$locale/school/teachers'
  | '/$locale/school/classes'
  | '/$locale/school/students'
  | '/$locale/teacher/classes'
  | '/$locale/teacher/assignments'
  | '/$locale/teacher/messages'
  | '/$locale/teacher/tasks';

export interface NavEntry {
  /** Route path under /$locale, e.g. '' for the index or 'kids'. */
  to: NavTo;
  labelKey: string;
  icon: string;
  /** Greyed-out, lands in a later phase. */
  soon?: boolean;
}

export function SidebarShell({
  titleKey,
  nav,
  children,
}: {
  titleKey: string;
  nav: NavEntry[];
  children: ReactNode;
}) {
  const t = useT();
  const { isRtl } = useLocale();
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);
  const { user, signOut } = useAuth();

  return (
    <div className="grid min-h-dvh grid-cols-[16rem_1fr]" dir={isRtl ? 'rtl' : 'ltr'}>
      <aside className="flex flex-col bg-burgundy-700 text-cream-50">
        <div className="flex h-16 items-center gap-3 px-6">
          <img src="/logo.png" alt="" className="h-10 w-10 rounded-full bg-cream-50 p-1" />
          <span className="font-semibold">{t('app.common.app_name')}</span>
        </div>
        <nav className="flex-1 px-3 py-4">
          {nav.map((entry) => (
            <NavItem key={`${entry.to}:${entry.labelKey}`} entry={entry} locale={locale} />
          ))}
        </nav>
        <div className="px-6 py-4 text-xs text-cream-100/60">{t(titleKey)}</div>
      </aside>

      <div className="flex flex-col">
        <header className="flex h-16 items-center justify-between border-b border-burgundy-500/10 bg-cream-50 px-6">
          <p className="text-xs uppercase tracking-wider text-ink-500">{t(titleKey)}</p>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <RoleSwitcher />
            <LangSwitcher locale={locale} ariaLabel={t('app.common.lang_switch_label')} />
            <span className="hidden text-sm text-ink-700 sm:inline">
              {user?.displayName ?? user?.email ?? user?.username}
            </span>
            <Button variant="secondary" size="sm" onClick={() => void signOut()}>
              {t('app.common.sign_out')}
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto bg-cream-50">{children}</main>
      </div>
    </div>
  );
}

function NavItem({ entry, locale }: { entry: NavEntry; locale: 'ar' | 'en' }) {
  const t = useT();
  if (entry.soon) {
    return (
      <div className="flex items-center gap-3 rounded-2xl px-3 py-2 text-cream-100/40">
        <span aria-hidden="true">{entry.icon}</span>
        <span className="text-sm">{t(entry.labelKey)}</span>
        <span className="ms-auto text-[10px] uppercase tracking-wider text-cream-100/40">soon</span>
      </div>
    );
  }
  return (
    <Link
      to={entry.to}
      params={{ locale }}
      activeOptions={{ exact: entry.to === '/$locale' }}
      className="flex items-center gap-3 rounded-2xl px-3 py-2 text-cream-100 transition-colors hover:bg-burgundy-600 [&.active]:bg-burgundy-600"
      activeProps={{ className: 'active' }}
      preload="intent"
    >
      <span aria-hidden="true">{entry.icon}</span>
      <span className="text-sm font-medium">{t(entry.labelKey)}</span>
    </Link>
  );
}
